/**
 * /faq — full FAQ page (categorised, searchable).
 * ====================================================
 * Public, no-auth. SEO-friendly. Single source for every question
 * a prospect might ask. Categorised tab-strip + per-category
 * collapsible blocks, plus an inline search filter.
 *
 * Brand: Slate-950 base, Emerald/Cyan accents.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import {
  ALL_FAQS, FAQ_CATEGORIES,
} from '../data/faqs';
import {
  Search, ChevronDown, ArrowLeft, HelpCircle, ExternalLink,
  Mail, MessageSquare,
} from 'lucide-react';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQ = () => {
  const [activeCat, setActiveCat] = useState('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ALL_FAQS.filter((f) => {
      if (activeCat !== 'all' && f.category !== activeCat) return false;
      if (!q) return true;
      return (
        f.q.toLowerCase().includes(q) ||
        f.a.toLowerCase().includes(q)
      );
    });
  }, [activeCat, query]);

  const counts = useMemo(() => {
    const c = { all: ALL_FAQS.length };
    FAQ_CATEGORIES.forEach((cat) => {
      c[cat.key] = ALL_FAQS.filter((f) => f.category === cat.key).length;
    });
    return c;
  }, []);

  return (
    <div className="dark min-h-screen bg-slate-900/60 text-white" data-testid="faq-page">
      <SEOHead
        title="FAQ · CARTIEAI"
        description="Common questions about CARTIEAI — pricing, security, SDK integration, free tools, support, data privacy. All answered honestly."
      />

      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-slate-950/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-1.5" data-testid="faq-back-home">
            <ArrowLeft className="w-3.5 h-3.5" /> CARTIEAI
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/pricing" className="text-sm text-slate-300 hover:text-white">Pricing</Link>
            <Link to="/trust" className="text-sm text-slate-300 hover:text-white">Trust</Link>
            <Link to="/login">
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="faq-nav-login">
                Log in
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12 space-y-10">
        {/* Hero */}
        <section className="text-center sm:text-left">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 mb-3">
            <HelpCircle className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-[11px] font-semibold tracking-widest text-emerald-300 uppercase">
              FAQ · {ALL_FAQS.length} answers
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white"><BrandTitle>Everything people ask before signing up.</BrandTitle></h1>
          <p className="text-slate-300 mt-3 max-w-2xl text-base">
            Pricing, security, integrations, free tools, refunds, support — all answered straight,
            no marketing fluff. If your question isn't here, email{' '}
            <a href="mailto:hello@cartieai.com" className="text-emerald-400 hover:text-emerald-300 underline-offset-4 hover:underline">hello@cartieai.com</a>{' '}
            and we'll add it.
          </p>
        </section>

        {/* Search */}
        <section>
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search FAQs (e.g. SOC 2, refunds, Stripe key…)"
              className="pl-9 bg-slate-900 border-white/10 text-white placeholder:text-slate-500"
              data-testid="faq-search-input"
            />
          </div>
        </section>

        {/* Category strip */}
        <section className="flex flex-wrap gap-2" data-testid="faq-category-strip">
          <button
            onClick={() => setActiveCat('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
              activeCat === 'all'
                ? 'bg-emerald-500 text-slate-950 border-emerald-400'
                : 'bg-slate-900 text-slate-200 border-white/10 hover:border-emerald-500/40'
            }`}
            data-testid="faq-cat-all"
          >
            All <span className="opacity-70">· {counts.all}</span>
          </button>
          {FAQ_CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setActiveCat(c.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                activeCat === c.key
                  ? 'bg-emerald-500 text-slate-950 border-emerald-400'
                  : 'bg-slate-900 text-slate-200 border-white/10 hover:border-emerald-500/40'
              }`}
              data-testid={`faq-cat-${c.key}`}
            >
              {c.label} <span className="opacity-70">· {counts[c.key] || 0}</span>
            </button>
          ))}
        </section>

        {/* List */}
        <section>
          {visible.length === 0 ? (
            <Card className="bg-slate-900/60 border border-white/10 shadow-md shadow-slate-900/40">
              <CardContent className="p-10 text-center" data-testid="faq-empty">
                <Search className="w-8 h-8 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-300 font-medium">Nothing matches that yet.</p>
                <p className="text-slate-400 text-sm mt-1">
                  Try a different search, or{' '}
                  <a href="mailto:hello@cartieai.com" className="text-emerald-400 hover:text-emerald-300">
                    ask us directly
                  </a>.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="divide-y divide-white/5 border border-white/10 rounded-xl bg-slate-900/60 shadow-md shadow-slate-900/40 overflow-hidden" data-testid="faq-list">
              {visible.map((f) => {
                const open = openId === f.id;
                const cat = FAQ_CATEGORIES.find((c) => c.key === f.category);
                return (
                  <div key={f.id} id={f.id} data-testid={`faq-item-${f.id}`}>
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => {
                        const willOpen = !open;
                        setOpenId(open ? null : f.id);
                        // v35.34 — fire-and-forget telemetry feeds TopAskedFaqsWidget.
                        // Only on open (not close); silently swallow any errors so a
                        // telemetry failure never blocks the user.
                        if (willOpen) {
                          axios
                            .post(`${API}/faq/track-click`, { faq_id: f.id, source_page: 'faq' })
                            .catch(() => {});
                        }
                      }}
                      className="w-full flex items-start justify-between gap-4 text-left px-5 py-4 hover:bg-emerald-500/5 transition-colors group"
                      data-testid={`faq-trigger-${f.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {cat && (
                            <Badge
                              variant="outline"
                              className="border-emerald-400/30 text-emerald-200 text-[11px] uppercase tracking-wider"
                            >
                              {cat.label}
                            </Badge>
                          )}
                        </div>
                        <span className="text-white font-medium text-sm sm:text-base group-hover:text-emerald-300">
                          {f.q}
                        </span>
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 flex-shrink-0 mt-1 text-slate-400 group-hover:text-emerald-300 transition-transform ${
                          open ? 'rotate-180 text-emerald-300' : ''
                        }`}
                      />
                    </button>
                    <div
                      role="region"
                      aria-hidden={!open}
                      className={`overflow-hidden transition-all duration-200 ease-out ${
                        open ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="px-5 pb-5 -mt-1 text-slate-300 text-sm leading-relaxed border-l-2 border-emerald-500/30 ml-5">
                        <p className="pl-3" data-testid={`faq-answer-${f.id}`}>{f.a}</p>
                        {f.links?.length > 0 && (
                          <div className="pl-3 pt-3 flex flex-wrap gap-3">
                            {f.links.map((l) => (
                              l.href.startsWith('/') && !l.href.startsWith('/api') ? (
                                <Link
                                  key={l.href}
                                  to={l.href}
                                  className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-xs font-medium"
                                >
                                  {l.label} <ExternalLink className="w-3 h-3" />
                                </Link>
                              ) : (
                                <a
                                  key={l.href}
                                  href={l.href}
                                  className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-xs font-medium"
                                >
                                  {l.label} <ExternalLink className="w-3 h-3" />
                                </a>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Still have questions */}
        <section>
          <Card className="bg-slate-900/60 border-emerald-500/30 shadow-md shadow-emerald-500/10">
            <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white">Still have questions?</h3>
                <p className="text-slate-300 text-sm mt-1 max-w-md">
                  Real humans answer within 4 business hours. No bots, no chat funnel — just direct email.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <a href="mailto:hello@cartieai.com">
                  <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold shadow-md shadow-emerald-500/20" data-testid="faq-email-cta">
                    <Mail className="w-4 h-4 mr-1.5" /> Email us
                  </Button>
                </a>
                <Link to="/contact">
                  <Button variant="outline" className="border-emerald-400/40 bg-emerald-500/5 text-emerald-200 hover:text-white hover:bg-emerald-500/15" data-testid="faq-contact-cta">
                    <MessageSquare className="w-4 h-4 mr-1.5" /> Contact form
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Forward navigation — clear back-to-home (Feb 24 2026) */}
        <section className="mt-8 flex flex-col sm:flex-row gap-3 items-start">
          <Link to="/">
            <Button size="lg" variant="outline" className="border-white/10 bg-transparent text-slate-400 hover:bg-white/5 hover:text-white px-6 h-12" data-testid="faq-cta-home">
              ← Back to home
            </Button>
          </Link>
          <Link to="/pricing">
            <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 h-12 shadow-md shadow-emerald-500/20 transition-all hover:-translate-y-0.5" data-testid="faq-cta-pricing">
              See pricing
            </Button>
          </Link>
        </section>
      </main>
    </div>
  );
};

export default FAQ;
